import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Button, Chip, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, IconButton, Tooltip, TextField,
  MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Alert, CircularProgress,
  InputAdornment, Divider,
} from '@mui/material';
import { Add, Delete, Refresh, People, PersonAdd } from '@mui/icons-material';
import { projectService, ProjectResource, Project, User } from '../../services/projectService';
import { formatCurrency } from '../../utils/currency';

const typeColors: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  EMPLOYEE: 'primary', CONTRACTOR: 'secondary', EQUIPMENT: 'info', OTHER: 'default',
};

const ResourcesPage: React.FC = () => {
  const [resources, setResources] = useState<ProjectResource[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedProject, setSelectedProject] = useState<number | ''>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Assign resource dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    projectId: '', userId: '', resourceType: 'EMPLOYEE', role: '',
    hourlyRate: '', allocationPercent: '100',
  });
  const [formLoading, setFormLoading] = useState(false);

  // Add new resource dialog
  const [newResourceDialogOpen, setNewResourceDialogOpen] = useState(false);
  const [newResourceData, setNewResourceData] = useState({
    fullName: '', email: '', role: 'USER',
  });
  const [newResourceLoading, setNewResourceLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [projectsData, usersData] = await Promise.all([
        projectService.getProjects(),
        projectService.getUsers(),
      ]);
      setProjects(projectsData);
      setUsers(usersData);

      if (selectedProject) {
        const resourcesData = await projectService.getProjectResources(selectedProject);
        setResources(resourcesData);
      } else {
        // Fetch resources for all projects
        const allResources: ProjectResource[] = [];
        for (const project of projectsData.slice(0, 5)) { // Limit to first 5 for performance
          try {
            const res = await projectService.getProjectResources(project.id);
            allResources.push(...res.map(r => ({ ...r, project_name: project.project_name })));
          } catch (e) { /* ignore */ }
        }
        setResources(allResources);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load resources');
    } finally {
      setLoading(false);
    }
  }, [selectedProject]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleOpenDialog = () => {
    setFormData({
      projectId: selectedProject ? selectedProject.toString() : '',
      userId: '', resourceType: 'EMPLOYEE', role: '', hourlyRate: '', allocationPercent: '100',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.userId || !formData.projectId) {
      setError('Resource and project are required');
      return;
    }
    setFormLoading(true);
    try {
      const selectedUser = users.find(u => u.id === parseInt(formData.userId));
      await projectService.assignResource(parseInt(formData.projectId), {
        userId: parseInt(formData.userId),
        resourceName: selectedUser?.full_name,
        resourceType: formData.resourceType,
        role: formData.role || undefined,
        hourlyRate: formData.hourlyRate ? parseFloat(formData.hourlyRate) : undefined,
        allocationPercent: parseInt(formData.allocationPercent),
      });
      setDialogOpen(false);
      fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to save resource');
    } finally {
      setFormLoading(false);
    }
  };

  const handleOpenNewResourceDialog = () => {
    setNewResourceData({ fullName: '', email: '', role: 'USER' });
    setNewResourceDialogOpen(true);
  };

  const handleSaveNewResource = async () => {
    if (!newResourceData.fullName.trim() || !newResourceData.email.trim()) {
      setError('Name and email are required');
      return;
    }
    setNewResourceLoading(true);
    try {
      await projectService.createUser(newResourceData);
      setNewResourceDialogOpen(false);
      fetchData(); // Refresh to get updated user list
    } catch (err: any) {
      setError(err.message || 'Failed to create resource');
    } finally {
      setNewResourceLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Remove this resource?')) return;
    try {
      await projectService.removeResource(id);
      fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to remove resource');
    }
  };

  const activeResources = resources.filter(r => r.is_active);
  const totalAllocation = resources.reduce((sum, r) => sum + (r.allocation_percent || 0), 0);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 'bold' }}>Resource Management</Typography>
          <Typography variant="body2" color="text.secondary">Manage project team members and resources</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh"><IconButton onClick={fetchData}><Refresh /></IconButton></Tooltip>
          <Button variant="outlined" startIcon={<Add />} onClick={handleOpenNewResourceDialog}>Add Resource</Button>
          <Button variant="contained" startIcon={<PersonAdd />} onClick={handleOpenDialog}>Assign to Project</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card><CardContent sx={{ textAlign: 'center', py: 2 }}>
            <People color="primary" sx={{ fontSize: 32, mb: 1 }} />
            <Typography variant="h5" fontWeight="bold">{activeResources.length}</Typography>
            <Typography variant="caption" color="text.secondary">Active Resources</Typography>
          </CardContent></Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card><CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h5" fontWeight="bold">{resources.filter(r => r.resource_type === 'EMPLOYEE').length}</Typography>
            <Typography variant="caption" color="text.secondary">Employees</Typography>
          </CardContent></Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card><CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h5" fontWeight="bold">{resources.filter(r => r.resource_type === 'CONTRACTOR').length}</Typography>
            <Typography variant="caption" color="text.secondary">Contractors</Typography>
          </CardContent></Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card><CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h5" fontWeight="bold">{Math.round(totalAllocation / Math.max(resources.length, 1))}%</Typography>
            <Typography variant="caption" color="text.secondary">Avg Allocation</Typography>
          </CardContent></Card>
        </Grid>
      </Grid>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField select fullWidth size="small" label="Filter by Project" value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value === '' ? '' : parseInt(e.target.value as string))}>
              <MenuItem value="">All Projects</MenuItem>
              {projects.map(p => <MenuItem key={p.id} value={p.id}>{p.project_name}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="body2" color="text.secondary">Showing {resources.length} resources</Typography>
          </Grid>
        </Grid>
      </Paper>

      {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box> : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.50' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Resource Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Role</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Allocation</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">Hourly Rate</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Hours Logged</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {resources.length === 0 ? (
                  <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No resources found</Typography>
                  </TableCell></TableRow>
                ) : resources.map(resource => (
                  <TableRow key={resource.id} sx={{ '&:hover': { bgcolor: 'grey.50' } }}>
                    <TableCell>
                      <Typography fontWeight="medium">{resource.resource_name}</Typography>
                      {resource.user_email && <Typography variant="caption" color="text.secondary">{resource.user_email}</Typography>}
                    </TableCell>
                    <TableCell><Chip label={resource.resource_type} size="small" color={typeColors[resource.resource_type]} /></TableCell>
                    <TableCell>{resource.role || '-'}</TableCell>
                    <TableCell>{resource.allocation_percent}%</TableCell>
                    <TableCell align="right">{resource.hourly_rate ? formatCurrency(resource.hourly_rate) : '-'}</TableCell>
                    <TableCell>{Number(resource.total_hours_logged || 0).toFixed(1)}h</TableCell>
                    <TableCell><Chip label={resource.is_active ? 'Active' : 'Inactive'} size="small" color={resource.is_active ? 'success' : 'default'} /></TableCell>
                    <TableCell>
                      <Tooltip title="Remove"><IconButton size="small" color="error" onClick={() => handleDelete(resource.id)}><Delete fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Assign Resource to Project Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Resource to Project</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <TextField select fullWidth label="Project" value={formData.projectId}
                onChange={(e) => setFormData({ ...formData, projectId: e.target.value })} required>
                <MenuItem value="">Select Project</MenuItem>
                {projects.map(p => <MenuItem key={p.id} value={p.id.toString()}>{p.project_name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField select fullWidth label="Resource" value={formData.userId}
                onChange={(e) => setFormData({ ...formData, userId: e.target.value })} required>
                <MenuItem value="">Select Resource</MenuItem>
                {users.map(u => <MenuItem key={u.id} value={u.id.toString()}>{u.full_name} ({u.email})</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField select fullWidth label="Type" value={formData.resourceType}
                onChange={(e) => setFormData({ ...formData, resourceType: e.target.value })}>
                <MenuItem value="EMPLOYEE">Employee</MenuItem>
                <MenuItem value="CONTRACTOR">Contractor</MenuItem>
                <MenuItem value="EQUIPMENT">Equipment</MenuItem>
                <MenuItem value="OTHER">Other</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField fullWidth label="Role" value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField fullWidth label="Hourly Rate" type="number" value={formData.hourlyRate}
                onChange={(e) => setFormData({ ...formData, hourlyRate: e.target.value })}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField fullWidth label="Allocation %" type="number" value={formData.allocationPercent}
                onChange={(e) => setFormData({ ...formData, allocationPercent: e.target.value })}
                inputProps={{ min: 0, max: 100 }} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={formLoading}>{formLoading ? 'Saving...' : 'Assign'}</Button>
        </DialogActions>
      </Dialog>

      {/* Add New Resource Dialog */}
      <Dialog open={newResourceDialogOpen} onClose={() => setNewResourceDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Resource</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create a new user/resource that can be assigned to projects.
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth label="Full Name" value={newResourceData.fullName}
                onChange={(e) => setNewResourceData({ ...newResourceData, fullName: e.target.value })} required />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth label="Email" type="email" value={newResourceData.email}
                onChange={(e) => setNewResourceData({ ...newResourceData, email: e.target.value })} required />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField select fullWidth label="System Role" value={newResourceData.role}
                onChange={(e) => setNewResourceData({ ...newResourceData, role: e.target.value })}>
                <MenuItem value="USER">User</MenuItem>
                <MenuItem value="PROJECT_MANAGER">Project Manager</MenuItem>
                <MenuItem value="FINANCE">Finance</MenuItem>
                <MenuItem value="SALES">Sales</MenuItem>
                <MenuItem value="PROCUREMENT">Procurement</MenuItem>
                <MenuItem value="ADMIN">Admin</MenuItem>
              </TextField>
            </Grid>
          </Grid>
          <Alert severity="info" sx={{ mt: 2 }}>
            Default password will be set to <strong>Welcome@123</strong>. User should change it on first login.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewResourceDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveNewResource} variant="contained" disabled={newResourceLoading}>
            {newResourceLoading ? 'Creating...' : 'Create Resource'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ResourcesPage;
